import { v4 as uuidv4 } from 'uuid';

export type FieldType = "number" | "boolean" | "timestamp" | "text" | "array_text" | "jsonb";
export type FieldTable = "order" | "customer";
export interface AvailableField {
  label: string;
  value: string;
  type: FieldType;
  table: FieldTable;
}

export const availableFields: AvailableField[] = [
  // From allorders table
  { label: 'Order ID', value: 'order.order_id', type: 'number', table: 'order' },
  { label: 'Order Number', value: 'order.order_no', type: 'number', table: 'order' },
  { label: 'Promise Time', value: 'order.promise_time', type: 'timestamp', table: 'order' },
  { label: 'Order Type', value: 'order.order_type', type: 'text', table: 'order' },
  { label: 'Customer Phone (Order)', value: 'order.customer_phone', type: 'text', table: 'order' },
  { label: 'Taken By', value: 'order.taken_by', type: 'text', table: 'order' },
  { label: 'Subtotal', value: 'order.subtotal', type: 'number', table: 'order' },
  { label: 'Discount', value: 'order.discount', type: 'number', table: 'order' },
  { label: 'Payment Amount', value: 'order.payment_amount', type: 'number', table: 'order' },
  { label: 'Created At (Order)', value: 'order.created_at', type: 'timestamp', table: 'order' },
  { label: 'Updated At (Order)', value: 'order.updated_at', type: 'timestamp', table: 'order' },
  { label: 'Payment Type ID', value: 'order.payment_type_id', type: 'number', table: 'order' },
  { label: 'Notified', value: 'order.notified', type: 'boolean', table: 'order' },
  { label: 'Is Prepaid', value: 'order.is_prepaid', type: 'boolean', table: 'order' },
  { label: 'Notified Timestamp', value: 'order.notified_timestamp', type: 'timestamp', table: 'order' },
  { label: 'Offer ID', value: 'order.offer_id', type: 'text', table: 'order' }, // UUIDs can be treated as text for comparison
  { label: 'Offer Tier ID', value: 'order.offer_tier_id', type: 'number', table: 'order' },
  { label: 'Order Tags', value: 'order.order_tags', type: 'array_text', table: 'order' },
  { label: 'Store ID', value: 'order.store_id', type: 'text', table: 'order' },
  { label: 'Offer Claimed', value: 'order.offer_claimed', type: 'boolean', table: 'order' },
  { label: 'Order Confirmed Notified', value: 'order.order_confirmed_notified', type: 'boolean', table: 'order' },
  { label: 'Offer Sent Notified', value: 'order.offer_sent_notified', type: 'boolean', table: 'order' },
  { label: 'Order Ready Notified', value: 'order.order_ready_notified', type: 'boolean', table: 'order' },
  { label: 'Order Confirmed Timestamp', value: 'order.order_confirmed_timestamp', type: 'timestamp', table: 'order' },
  { label: 'Offer Sent Timestamp', value: 'order.offer_sent_timestamp', type: 'timestamp', table: 'order' },
  { label: 'Order Ready Timestamp', value: 'order.order_ready_timestamp', type: 'timestamp', table: 'order' },
  { label: 'Review Request Sent Notified', value: 'order.review_request_sent_notified', type: 'boolean', table: 'order' },
  { label: 'Review Request Sent Timestamp', value: 'order.review_request_sent_timestamp', type: 'timestamp', table: 'order' },
  { label: 'Items (JSONB)', value: 'order.items', type: 'jsonb', table: 'order' }, // Special type for jsonb
  { label: 'Offer Items Claimed (JSONB)', value: 'order.offer_items_claimed', type: 'jsonb', table: 'order' }, // Special type for jsonb

  // From customers table
  { label: 'Customer Phone', value: 'customer.customer_phone', type: 'text', table: 'customer' },
  { label: 'Customer Name', value: 'customer.customer_name', type: 'text', table: 'customer' },
  { label: 'Customer Email', value: 'customer.customer_email', type: 'text', table: 'customer' },
  { label: 'Is Loyalty Member', value: 'customer.is_loyalty', type: 'boolean', table: 'customer' },
  { label: 'Loyalty Join Date', value: 'customer.loyalty_join_date', type: 'timestamp', table: 'customer' },
  { label: 'Customer Tags', value: 'customer.customer_tags', type: 'array_text', table: 'customer' },
  { label: 'Total Orders Amount', value: 'customer.total_orders_amount', type: 'number', table: 'customer' },
  { label: 'Created At (Customer)', value: 'customer.created_at', type: 'timestamp', table: 'customer' },
  { label: 'Updated At (Customer)', value: 'customer.updated_at', type: 'timestamp', table: 'customer' },
  { label: 'Offer History (JSONB)', value: 'customer.offer_history', type: 'jsonb', table: 'customer' }, // Special type for jsonb
];

export const availableOperators = {
  text: [
    { label: 'Equals', value: 'equals' },
    { label: 'Does Not Equal', value: 'not_equals' },
    { label: 'Contains', value: 'contains' },
    { label: 'Starts With', value: 'starts_with' },
    { label: 'Ends With', value: 'ends_with' },
    { label: 'Is Empty', value: 'is_empty' },
    { label: 'Is Not Empty', value: 'is_not_empty' },
  ],
  number: [
    { label: 'Equals', value: 'equals' },
    { label: 'Does Not Equal', value: 'not_equals' },
    { label: 'Greater Than', value: 'greater_than' },
    { label: 'Less Than', value: 'less_than' },
    { label: 'Greater Than or Equal To', value: 'greater_than_or_equal_to' },
    { label: 'Less Than or Equal To', value: 'less_than_or_equal_to' },
    { label: 'Is Between', value: 'is_between' },
  ],
  boolean: [
    { label: 'Is True', value: 'is_true' },
    { label: 'Is False', value: 'is_false' },
  ],
  timestamp: [
    { label: 'Is On', value: 'is_on' },
    { label: 'Is Before', value: 'is_before' },
    { label: 'Is After', value: 'is_after' },
    // 'is_between' would require a more complex input (two date pickers)
  ],
  array_text: [
    { label: 'Contains Any Of', value: 'contains_any' },
    { label: 'Contains All Of', value: 'contains_all' },
    { label: 'Is Empty', value: 'is_empty' },
    { label: 'Is Not Empty', value: 'is_not_empty' },
  ],
  jsonb: [
    { label: 'Has Key', value: 'has_key' },
    { label: 'Has Any Key', value: 'has_any_key' },
    { label: 'Has All Keys', value: 'has_all_keys' },
    { label: 'Contains Item ID', value: 'contains_item_id' },
    { label: 'Contains Any Item IDs from List', value: 'contains_any_item_ids' },
    // More specific JSONB operators like 'contains_json_object' would be defined here
  ]
};
